/**
 * Algoritmo Avanzado de Distribución de Presupuesto
 * Basado en el sistema de BudgetControl con mejoras para GalaxyMetrics
 * 
 * Características principales:
 * - Prorrateo por segundos exactos del primer y último día
 * - Redistribución inteligente entre días
 * - Manejo correcto de días laborales (5, 6, 7 días/semana)
 * - Cálculo de semanas ISO (lunes a domingo)
 */

export class AdvancedBudgetCalculator {
  
  /**
   * Calcula la distribución avanzada de presupuesto
   * @param {Object} params - Parámetros de cálculo
   * @param {Date} params.dateFrom - Fecha de inicio
   * @param {Date} params.dateTo - Fecha de fin
   * @param {number} params.totalAmount - Monto total a distribuir
   * @param {number} params.daysPerWeek - Días por semana (5, 6, 7)
   * @returns {Object} Distribución completa del presupuesto
   */
  static calculateAdvancedBudgetDistribution({ dateFrom, dateTo, totalAmount, daysPerWeek = 7 }) {
    try {
      // Validaciones básicas
      if (!dateFrom || !dateTo || totalAmount <= 0) {
        return this.getFallbackCalculation(dateFrom, dateTo, totalAmount, daysPerWeek);
      }

      // Asegurar que dateFrom y dateTo son objetos Date
      const startDate = new Date(dateFrom);
      const endDate = new Date(dateTo);
      
      if (startDate > endDate) {
        return this.getFallbackCalculation(dateFrom, dateTo, totalAmount, daysPerWeek);
      }

      // Paso 1: Calcular segundos del primer y último día
      const firstDaySeconds = this.calculateFirstDaySeconds(startDate, daysPerWeek);
      const lastDaySeconds = this.calculateLastDaySeconds(endDate, daysPerWeek);

      // Paso 2: Generar fechas efectivas
      const effectiveDates = this.generateEffectiveDates(startDate, endDate, daysPerWeek);
      const totalEffectiveDays = effectiveDates.length;

      if (totalEffectiveDays === 0) {
        return this.getFallbackCalculation(dateFrom, dateTo, totalAmount, daysPerWeek);
      }

      // Paso 3: Aplicar algoritmo específico según número de días
      let distribution;
      if (totalEffectiveDays === 1) {
        distribution = this.calculateOneDayDistribution(totalAmount, firstDaySeconds);
      } else if (totalEffectiveDays === 2) {
        distribution = this.calculateTwoDaysDistribution(
          totalAmount, firstDaySeconds, lastDaySeconds, totalEffectiveDays
        );
      } else {
        distribution = this.calculateMultipleDaysDistribution(
          totalAmount, firstDaySeconds, lastDaySeconds, totalEffectiveDays
        );
      }

      // Paso 4: Calcular información adicional
      const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
      const totalWeeks = Math.ceil(totalDays / 7);
      const weeklyAmount = totalAmount / totalWeeks;

      return {
        dailyAmount: distribution.dailyAmountAdjusted,
        weeklyAmount: weeklyAmount,
        totalDays,
        totalWeeks,
        effectiveDays: totalEffectiveDays,
        firstDayAmount: distribution.firstDayAmount,
        lastDayAmount: distribution.lastDayAmount,
        dailyAmountBase: distribution.dailyAmountBase,
        dailyAmountAdjusted: distribution.dailyAmountAdjusted,
        weeklyAmountEstimate: weeklyAmount,
        effectiveDaysPerWeek: daysPerWeek,
        
        // Información detallada para tooltips
        dailyBreakdown: distribution.dailyBreakdown,
        weeklyBreakdown: this.calculateWeeklyBreakdown(effectiveDates, distribution, totalAmount),
        
        // Resumen para UI
        summary: {
          display: `${totalEffectiveDays} días efectivos • ${totalWeeks} semanas • ${daysPerWeek}/semana`,
          weeklyDisplay: `$${weeklyAmount.toFixed(2)}/semana • ${daysPerWeek} días laborales`,
          firstLastInfo: `Primer día: $${distribution.firstDayAmount.toFixed(2)} • Último día: $${distribution.lastDayAmount.toFixed(2)}`
        },

        // Debug info
        debug: {
          algorithm: totalEffectiveDays === 1 ? 'one-day' : totalEffectiveDays === 2 ? 'two-days' : 'multiple-days',
          firstDaySeconds,
          lastDaySeconds,
          effectiveDates: effectiveDates.map(d => d.toISOString().split('T')[0])
        }
      };

    } catch (error) {
      console.error('Error en calculateAdvancedBudgetDistribution:', error);
      return this.getFallbackCalculation(dateFrom, dateTo, totalAmount, daysPerWeek);
    }
  }

  /**
   * Calcula segundos efectivos del primer día
   */
  static calculateFirstDaySeconds(dateFrom, daysPerWeek) {
    const hour = dateFrom.getHours();
    const minute = dateFrom.getMinutes();
    
    // Segundos restantes del día (86400 total - segundos transcurridos)
    let firstDaySeconds = 86400 - (hour * 3600 + minute * 60);
    
    // Ajuste para días laborales
    if (daysPerWeek === 5) {
      const dayOfWeek = dateFrom.getDay(); // 0=Domingo, 6=Sábado
      if (dayOfWeek === 0 || dayOfWeek === 6) { // Sábado o Domingo
        firstDaySeconds = 86400; // Día completo al siguiente día hábil
      }
    } else if (daysPerWeek === 6) {
      const dayOfWeek = dateFrom.getDay();
      if (dayOfWeek === 0) { // Domingo
        firstDaySeconds = 86400; // Día completo al siguiente día hábil
      }
    }
    
    return firstDaySeconds;
  }

  /**
   * Calcula segundos efectivos del último día
   */
  static calculateLastDaySeconds(dateTo, daysPerWeek) {
    const hour = dateTo.getHours();
    const minute = dateTo.getMinutes();
    
    let lastDaySeconds;
    if (hour === 0 && minute === 0) {
      lastDaySeconds = 86400; // Día completo
    } else {
      lastDaySeconds = hour * 3600 + minute * 60;
    }
    
    // Ajuste para días laborales
    if (daysPerWeek === 5) {
      const dayOfWeek = dateTo.getDay();
      if (dayOfWeek === 0 || dayOfWeek === 6) { // Sábado o Domingo
        lastDaySeconds = 86400; // Día completo
      }
    } else if (daysPerWeek === 6) {
      const dayOfWeek = dateTo.getDay();
      if (dayOfWeek === 0) { // Domingo
        lastDaySeconds = 86400; // Día completo
      }
    }
    
    return lastDaySeconds;
  }

  /**
   * Genera array de fechas efectivas según días de la semana
   */
  static generateEffectiveDates(dateFrom, dateTo, daysPerWeek) {
    const dates = [];
    const current = new Date(dateFrom);
    
    while (current <= dateTo) {
      const dayOfWeek = current.getDay(); // 0=Domingo, 6=Sábado
      
      let isWorkingDay = true;
      if (daysPerWeek === 5) {
        isWorkingDay = dayOfWeek !== 0 && dayOfWeek !== 6; // Lun-Vie
      } else if (daysPerWeek === 6) {
        isWorkingDay = dayOfWeek !== 0; // Lun-Sáb
      }
      // daysPerWeek === 7: todos los días son efectivos
      
      if (isWorkingDay) {
        dates.push(new Date(current));
      }
      
      current.setDate(current.getDate() + 1);
    }
    
    return dates;
  }

  /**
   * Algoritmo para un solo día (BudgetControl)
   */
  static calculateOneDayDistribution(totalAmount, firstDaySeconds) {
    const proportion = firstDaySeconds / 86400;
    const finalAmount = totalAmount * proportion;
    
    return {
      dailyAmountBase: totalAmount,
      dailyAmountAdjusted: finalAmount,
      firstDayAmount: finalAmount,
      lastDayAmount: finalAmount,
      dailyBreakdown: [{
        day: 1,
        amount: finalAmount,
        seconds: firstDaySeconds,
        proportion: proportion
      }]
    };
  }

  /**
   * Algoritmo para dos días (BudgetControl)
   */
  static calculateTwoDaysDistribution(totalAmount, firstDaySeconds, lastDaySeconds, totalEffectiveDays) {
    const dailyAmountBase = totalAmount / totalEffectiveDays;
    
    // Calcular montos proporcionales por segundos
    const firstDayAmount = dailyAmountBase * (firstDaySeconds / 86400);
    const lastDayAmount = dailyAmountBase * (lastDaySeconds / 86400);
    
    // Redistribución: lo que sobra del primer día se suma al último
    const prorrateoFirstDay = (dailyAmountBase - firstDayAmount) / (totalEffectiveDays - 1);
    const prorrateoLastDay = (dailyAmountBase - lastDayAmount) / (totalEffectiveDays - 1);
    
    const finalFirstDayAmount = firstDayAmount + prorrateoLastDay;
    const finalLastDayAmount = lastDayAmount + prorrateoFirstDay;
    
    return {
      dailyAmountBase,
      dailyAmountAdjusted: dailyAmountBase + prorrateoFirstDay + prorrateoLastDay,
      firstDayAmount: finalFirstDayAmount,
      lastDayAmount: finalLastDayAmount,
      dailyBreakdown: [
        {
          day: 1,
          amount: finalFirstDayAmount,
          seconds: firstDaySeconds,
          proportion: firstDaySeconds / 86400,
          prorrateo: prorrateoLastDay
        },
        {
          day: 2,
          amount: finalLastDayAmount,
          seconds: lastDaySeconds,
          proportion: lastDaySeconds / 86400,
          prorrateo: prorrateoFirstDay
        }
      ]
    };
  }

  /**
   * Algoritmo para múltiples días (BudgetControl)
   */
  static calculateMultipleDaysDistribution(totalAmount, firstDaySeconds, lastDaySeconds, totalEffectiveDays) {
    const dailyAmountBase = totalAmount / totalEffectiveDays;
    
    // Calcular montos del primer y último día
    const firstDayAmount = dailyAmountBase * (firstDaySeconds / 86400);
    const lastDayAmount = dailyAmountBase * (lastDaySeconds / 86400);
    
    // Calcular prorrateos (algoritmo de BudgetControl)
    const prorrateoFirstDay = (dailyAmountBase - firstDayAmount) / (totalEffectiveDays - 1);
    const prorrateoLastDay = (dailyAmountBase - lastDayAmount) / (totalEffectiveDays - 1);
    
    // Redistribuir
    const finalFirstDayAmount = firstDayAmount + prorrateoLastDay;
    const finalLastDayAmount = lastDayAmount + prorrateoFirstDay;
    const dailyAmountAdjusted = dailyAmountBase + prorrateoFirstDay + prorrateoLastDay;
    
    return {
      dailyAmountBase,
      dailyAmountAdjusted,
      firstDayAmount: finalFirstDayAmount,
      lastDayAmount: finalLastDayAmount,
      dailyBreakdown: [
        {
          day: 1,
          amount: finalFirstDayAmount,
          seconds: firstDaySeconds,
          proportion: firstDaySeconds / 86400,
          prorrateo: prorrateoLastDay
        },
        ...Array.from({ length: totalEffectiveDays - 2 }, (_, i) => ({
          day: i + 2,
          amount: dailyAmountAdjusted,
          seconds: 86400,
          proportion: 1,
          prorrateo: prorrateoFirstDay + prorrateoLastDay
        })),
        {
          day: totalEffectiveDays,
          amount: finalLastDayAmount,
          seconds: lastDaySeconds,
          proportion: lastDaySeconds / 86400,
          prorrateo: prorrateoFirstDay
        }
      ]
    };
  }

  /**
   * Calcula distribución semanal
   */
  static calculateWeeklyBreakdown(effectiveDates, distribution, totalAmount) {
    const weeks = new Map();
    
    effectiveDates.forEach((date, index) => {
      // Obtener semana ISO (lunes a domingo) y año ISO
      const isoYear = this.getISOWeekYear(date);
      const weekNumber = this.getISOWeek(date);
      const weekKey = `${isoYear}-W${weekNumber.toString().padStart(2, '0')}`;
      
      if (!weeks.has(weekKey)) {
        weeks.set(weekKey, {
          weekNumber,
          year: isoYear,
          days: [],
          totalAmount: 0
        });
      }
      
      const dayAmount = index === 0 ? distribution.firstDayAmount :
                      index === effectiveDates.length - 1 ? distribution.lastDayAmount :
                      distribution.dailyAmountAdjusted;
      
      weeks.get(weekKey).days.push({
        date: new Date(date),
        amount: dayAmount
      });
      weeks.get(weekKey).totalAmount += dayAmount;
    });
    
    return Array.from(weeks.values());
  }

  /**
   * Obtiene el número de semana ISO
   */
  static getISOWeek(date) {
    const target = new Date(date.valueOf());
    const dayNumber = (date.getDay() + 6) % 7; // Lunes = 0
    target.setDate(target.getDate() - dayNumber + 3);
    const firstThursday = target.valueOf();
    target.setMonth(0, 1);
    if (target.getDay() !== 4) {
      target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
    }
    return 1 + Math.ceil((firstThursday - target) / 604800000);
  }

  /**
   * Obtiene el año ISO de la semana (equivalente a strftime %G)
   */
  static getISOWeekYear(date) {
    const d = new Date(date.valueOf());
    const dayNumber = (d.getDay() + 6) % 7; // Lunes = 0
    // Mover al jueves de la misma semana ISO
    d.setDate(d.getDate() - dayNumber + 3);
    return d.getFullYear();
  }

  /**
   * Cálculo de fallback si falla el algoritmo avanzado
   */
  static getFallbackCalculation(dateFrom, dateTo, totalAmount, daysPerWeek) {
    const startDate = dateFrom ? new Date(dateFrom) : new Date();
    const endDate = dateTo ? new Date(dateTo) : new Date();
    const totalDays = Math.max(1, Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1);
    const effectiveDays = Math.ceil((totalDays * daysPerWeek) / 7);
    const totalWeeks = Math.ceil(totalDays / 7);
    const dailyAmount = effectiveDays > 0 ? totalAmount / effectiveDays : 0;
    const weeklyAmount = totalWeeks > 0 ? totalAmount / totalWeeks : 0;

    return {
      dailyAmount: parseFloat(dailyAmount.toFixed(2)),
      weeklyAmount: parseFloat(weeklyAmount.toFixed(2)),
      totalDays,
      totalWeeks,
      effectiveDays,
      firstDayAmount: dailyAmount,
      lastDayAmount: dailyAmount,
      dailyAmountBase: dailyAmount,
      dailyAmountAdjusted: dailyAmount,
      weeklyAmountEstimate: weeklyAmount,
      effectiveDaysPerWeek: daysPerWeek,
      summary: {
        display: `${effectiveDays} días efectivos (fallback)`,
        weeklyDisplay: `$${weeklyAmount.toFixed(2)}/semana (fallback)`,
        firstLastInfo: `Distribución uniforme: $${dailyAmount.toFixed(2)}/día`
      }
    };
  }
}

/**
 * Función de conveniencia para cálculo rápido
 */
export function calculateAdvancedBudgetDistribution(params) {
  return AdvancedBudgetCalculator.calculateAdvancedBudgetDistribution(params);
}

export default AdvancedBudgetCalculator; 